import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, WifiOff, CloudUpload, X } from "lucide-react";
import { getQueue, initOfflineSync, flushQueue } from "@/lib/offline";

const DISMISS_KEY = "chefcheck.install_dismissed";

// FASE 14 — PWA install prompt + offline/sync status banner.
export const PwaManager = () => {
  const [installEvent, setInstallEvent] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [offline, setOffline] = useState(!navigator.onLine);
  const [pendingCount, setPendingCount] = useState(getQueue().length);

  useEffect(() => {
    initOfflineSync();

    const onPrompt = (e) => {
      e.preventDefault();
      setInstallEvent(e);
      if (!localStorage.getItem(DISMISS_KEY)) setShowInstall(true);
    };
    const onInstalled = () => {
      setShowInstall(false);
      setInstallEvent(null);
    };
    const onOnline = () => setOffline(false);
    const onOffline = () => setOffline(true);
    const onQueueChanged = (e) => setPendingCount(e.detail?.count ?? getQueue().length);

    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("chefcheck:queue-changed", onQueueChanged);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("chefcheck:queue-changed", onQueueChanged);
    };
  }, []);

  const install = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    await installEvent.userChoice;
    setShowInstall(false);
    setInstallEvent(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setShowInstall(false);
  };

  return (
    <>
      {offline && (
        <div
          data-testid="offline-banner"
          className="fixed top-0 inset-x-0 z-[100] bg-amber-600 text-white text-center text-xs font-semibold py-1.5 px-4 flex items-center justify-center gap-2"
        >
          <WifiOff className="h-3.5 w-3.5" />
          Sem conexão — checklists concluídos serão sincronizados automaticamente
          {pendingCount > 0 && (
            <span className="bg-white/20 rounded-full px-2 py-0.5">{pendingCount} pendente(s)</span>
          )}
        </div>
      )}

      {!offline && pendingCount > 0 && (
        <div
          data-testid="sync-pending-banner"
          className="fixed top-0 inset-x-0 z-[100] bg-emerald-700 text-white text-center text-xs font-semibold py-1.5 px-4 flex items-center justify-center gap-2"
        >
          <CloudUpload className="h-3.5 w-3.5 animate-pulse" />
          Sincronizando {pendingCount} checklist(s) pendente(s)...
          <button onClick={flushQueue} className="underline underline-offset-2">Tentar agora</button>
        </div>
      )}

      {showInstall && (
        <div
          data-testid="pwa-install-banner"
          className="fixed bottom-4 left-4 z-[100] max-w-sm bg-stone-950 text-white rounded-2xl shadow-2xl p-4 flex items-center gap-3 border border-stone-800"
        >
          <img src="/icon-192.png" alt="Ultra" className="h-12 w-12 rounded-xl" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm">Instalar Ultra</div>
            <div className="text-xs text-stone-400">
              Acesse checklists direto da tela inicial, mesmo offline.
            </div>
          </div>
          <Button
            size="sm"
            onClick={install}
            data-testid="pwa-install-btn"
            className="bg-emerald-600 hover:bg-emerald-500 text-white shrink-0"
          >
            <Download className="h-4 w-4 mr-1" /> Instalar
          </Button>
          <button onClick={dismiss} data-testid="pwa-install-dismiss" className="text-stone-500 hover:text-white shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </>
  );
};

export default PwaManager;
